import type { FC } from 'react'

import { EuroCents } from '@shared/domain/euro-cents'
import { PageTitle } from 'components/common/Titles'
import { FormattedAmount } from 'components/FormattedAmount'
import { getCartItemsTotal, getCartItemTotal } from 'domain/get-cart-items-total'
import { useTranslation } from 'react-i18next'

import {
  StyledPricePartials,
  StyledSummaryTitle,
  StyledTotal,
} from './styled-order-summary-components'

type SummaryLineItem = {
  name: string
  itemCount: number
  price: number
}

type SmallOrderSummaryProps = {
  cartItems: SummaryLineItem[]
  additionalItems: SummaryLineItem[]
  discount?: SummaryLineItem
}

export const toSummaryLineItem: (item: {
  name?: string | null
  itemCount?: string | number | null
  price?: number | null
}) => SummaryLineItem = (item) => ({
  name: item.name || '',
  price: item.price || 0,
  itemCount: Number(item.itemCount),
})

export const SmallOrderSummary: FC<SmallOrderSummaryProps> = ({
  cartItems,
  additionalItems,
  discount,
}) => {
  const { t } = useTranslation()
  const itemsTotal = getCartItemsTotal(cartItems)
  const total = getCartItemsTotal(additionalItems)
    .add(itemsTotal)
    .add(EuroCents.fromEuros(discount?.price || 0))

  return (
    <div data-test-id="small-order-summary">
      <PageTitle>{t('Summary')}</PageTitle>
      <StyledTotal>
        <StyledPricePartials data-test-id="cart-items-total">
          <div>{t('Shoppings')}</div>
          <FormattedAmount value={itemsTotal} />
        </StyledPricePartials>
        {additionalItems.map((product, k) => (
          <StyledPricePartials key={`line-item-${k}`} data-test-id="additional-line-item">
            <div>{product.name}</div>
            <FormattedAmount value={getCartItemTotal(product)} />
          </StyledPricePartials>
        ))}
        {discount && (
          <StyledPricePartials data-test-id="discount-code-line-item">
            <div>{discount.name}</div>
            {discount.price !== 0 ? <FormattedAmount value={getCartItemTotal(discount)} /> : '*'}
          </StyledPricePartials>
        )}
        <StyledSummaryTitle>
          {t('Summary price')}
          <FormattedAmount data-test-id="order-total" value={total} />
        </StyledSummaryTitle>
      </StyledTotal>
    </div>
  )
}
